import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { profileService } from "../../services/api";

type ApprovalStatus = "pending" | "approved" | "redirecting";

const PendingApproval: React.FC = () => {
    const { logout, user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState<ApprovalStatus>("pending");

    const checkApprovalStatus = useCallback(async () => {
        try {
            const profile = await profileService.get();
            if (profile.is_approved) {
                setStatus("approved");
                // Mettre à jour le user dans le contexte
                if (user) {
                    updateUser({ ...user, is_approved: true });
                }
                // Attendre l'animation puis rediriger
                setTimeout(() => {
                    setStatus("redirecting");
                    setTimeout(() => {
                        navigate("/products");
                    }, 1000);
                }, 2500);
            }
        } catch (error) {
            console.error("Erreur lors de la vérification du statut:", error);
        }
    }, [user, updateUser, navigate]);

    useEffect(() => {
        // Vérifier immédiatement au chargement
        checkApprovalStatus();

        // Puis vérifier toutes les 5 secondes
        const interval = setInterval(checkApprovalStatus, 5000);

        return () => clearInterval(interval);
    }, [checkApprovalStatus]);

    // Si déjà approuvé, rediriger
    useEffect(() => {
        if (user?.is_approved) {
            navigate("/products");
        }
    }, [user, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 overflow-hidden">
            {/* Background animated elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className={`absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse transition-colors duration-1000 ${
                        status === "approved" || status === "redirecting"
                            ? "bg-green-500/10"
                            : "bg-accent/5"
                    }`}
                    style={{ animationDuration: "4s" }}
                />
                <div
                    className={`absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse transition-colors duration-1000 ${
                        status === "approved" || status === "redirecting"
                            ? "bg-green-500/10"
                            : "bg-accent-light/5"
                    }`}
                    style={{ animationDuration: "6s", animationDelay: "2s" }}
                />
            </div>

            <div
                className={`card p-8 w-full max-w-lg text-center relative transition-all duration-500 ${
                    status === "redirecting"
                        ? "animate-[fadeOutUp_1s_ease-out_forwards]"
                        : "animate-[fadeInUp_0.6s_ease-out]"
                }`}
            >
                {/* Animated icon container */}
                <div className="mb-6">
                    <div className="relative w-24 h-24 mx-auto mb-4">
                        {status === "pending" ? (
                            <>
                                {/* Outer rotating ring */}
                                <div className="absolute inset-0 rounded-full border-2 border-accent/30 animate-[spin_8s_linear_infinite]" />
                                {/* Middle pulsing ring */}
                                <div className="absolute inset-2 rounded-full border-2 border-accent/20 animate-[ping_2s_ease-in-out_infinite]" />
                                {/* Inner circle with icon */}
                                <div className="absolute inset-3 bg-accent/20 rounded-full flex items-center justify-center animate-[pulse_2s_ease-in-out_infinite]">
                                    <svg
                                        className="w-10 h-10 text-accent"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                {/* Orbiting dot */}
                                <div className="absolute inset-0 animate-[spin_3s_linear_infinite]">
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-accent rounded-full shadow-[0_0_10px_rgba(0,188,212,0.5)]" />
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Approved state - Green celebration */}
                                <div className="absolute inset-0 rounded-full border-2 border-green-500/50 animate-[ping_1s_ease-out]" />
                                <div className="absolute inset-0 rounded-full border-2 border-green-500/30 animate-[ping_1s_ease-out_0.2s]" />
                                <div className="absolute inset-3 bg-green-500/20 rounded-full flex items-center justify-center animate-[scaleIn_0.5s_ease-out]">
                                    <svg
                                        className="w-10 h-10 text-green-500 animate-[checkmark_0.5s_ease-out_0.3s_both]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </div>
                                {/* Confetti-like particles */}
                                <div className="absolute inset-0">
                                    {[...Array(8)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="absolute w-2 h-2 bg-green-500 rounded-full animate-[confetti_1s_ease-out_forwards]"
                                            style={{
                                                left: "50%",
                                                top: "50%",
                                                animationDelay: `${i * 0.1}s`,
                                                transform: `rotate(${i * 45}deg)`,
                                            }}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    <h1
                        className={`text-2xl font-bold mb-2 transition-colors duration-500 ${
                            status === "pending"
                                ? "text-white"
                                : "text-green-400"
                        }`}
                    >
                        {status === "pending"
                            ? "Inscription en attente"
                            : status === "approved"
                              ? "Compte approuve !"
                              : "Redirection..."}
                    </h1>
                    <p className="text-gray-400">
                        {status === "pending" ? (
                            <>
                                Bonjour{" "}
                                <span className="text-accent font-medium">
                                    {user?.username}
                                </span>
                            </>
                        ) : (
                            <>
                                Bienvenue{" "}
                                <span className="text-green-400 font-medium">
                                    {user?.username}
                                </span>{" "}
                                !
                            </>
                        )}
                    </p>
                </div>

                {/* Info box with shimmer effect */}
                <div
                    className={`relative rounded-lg p-6 mb-6 overflow-hidden transition-colors duration-500 ${
                        status === "pending"
                            ? "bg-primary-dark/50"
                            : "bg-green-500/10 border border-green-500/30"
                    }`}
                >
                    {status === "pending" && (
                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_3s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
                    )}

                    {status === "pending" ? (
                        <>
                            <p className="text-gray-300 mb-4 relative z-10">
                                Votre inscription a bien ete enregistree et est
                                en cours de verification par notre equipe.
                            </p>
                            <p className="text-gray-400 text-sm relative z-10">
                                Un administrateur doit valider votre compte
                                avant que vous puissiez acceder aux produits.
                                Vous recevrez un acces complet une fois votre
                                compte approuve.
                            </p>
                        </>
                    ) : (
                        <p className="text-green-300 relative z-10">
                            Votre compte a ete valide par notre equipe. Vous
                            allez etre redirige vers les produits...
                        </p>
                    )}
                </div>

                {/* Progress steps with staggered animations */}
                <div className="space-y-4">
                    {/* Step 1 - Completed */}
                    <div className="flex items-center justify-center space-x-3">
                        <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                                <svg
                                    className="w-5 h-5 text-green-500"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            {status === "pending" && (
                                <div className="absolute inset-0 rounded-full bg-green-500/30 animate-[ping_2s_ease-in-out_infinite]" />
                            )}
                        </div>
                        <span className="text-green-400 text-sm font-medium">
                            Inscription soumise
                        </span>
                    </div>

                    {/* Connecting line */}
                    <div
                        className={`w-0.5 h-4 mx-auto transition-colors duration-500 ${
                            status === "pending"
                                ? "bg-gradient-to-b from-green-500/50 to-accent/50"
                                : "bg-gradient-to-b from-green-500/50 to-green-500/50"
                        }`}
                    />

                    {/* Step 2 - Verification */}
                    <div className="flex items-center justify-center space-x-3">
                        <div className="relative">
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-500 ${
                                    status === "pending"
                                        ? "bg-accent/20"
                                        : "bg-green-500/20"
                                }`}
                            >
                                {status === "pending" ? (
                                    <svg
                                        className="w-5 h-5 text-accent animate-[spin_2s_linear_infinite]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className="w-5 h-5 text-green-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                )}
                            </div>
                            {status === "pending" && (
                                <div className="absolute inset-0 rounded-full border-2 border-accent/50 animate-[ping_1.5s_ease-in-out_infinite]" />
                            )}
                        </div>
                        <span
                            className={`text-sm font-medium transition-colors duration-500 ${
                                status === "pending"
                                    ? "text-accent"
                                    : "text-green-400"
                            }`}
                        >
                            {status === "pending"
                                ? "Verification en cours"
                                : "Verification terminee"}
                        </span>
                        {status === "pending" && (
                            <div className="flex space-x-1">
                                <span
                                    className="w-1.5 h-1.5 bg-accent rounded-full animate-[bounce_1s_ease-in-out_infinite]"
                                    style={{ animationDelay: "0s" }}
                                />
                                <span
                                    className="w-1.5 h-1.5 bg-accent rounded-full animate-[bounce_1s_ease-in-out_infinite]"
                                    style={{ animationDelay: "0.2s" }}
                                />
                                <span
                                    className="w-1.5 h-1.5 bg-accent rounded-full animate-[bounce_1s_ease-in-out_infinite]"
                                    style={{ animationDelay: "0.4s" }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Connecting line */}
                    <div
                        className={`w-0.5 h-4 mx-auto transition-colors duration-500 ${
                            status === "pending"
                                ? "bg-gradient-to-b from-accent/50 to-gray-500/30"
                                : "bg-gradient-to-b from-green-500/50 to-green-500/50"
                        }`}
                    />

                    {/* Step 3 - Access */}
                    <div className="flex items-center justify-center space-x-3">
                        <div className="relative">
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-500 ${
                                    status === "pending"
                                        ? "bg-gray-500/20"
                                        : "bg-green-500/20"
                                }`}
                            >
                                {status === "pending" ? (
                                    <svg
                                        className="w-5 h-5 text-gray-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className="w-5 h-5 text-green-500"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                )}
                            </div>
                            {status !== "pending" && (
                                <div className="absolute inset-0 rounded-full bg-green-500/30 animate-[ping_1s_ease-in-out]" />
                            )}
                        </div>
                        <span
                            className={`text-sm font-medium transition-colors duration-500 ${
                                status === "pending"
                                    ? "text-gray-500"
                                    : "text-green-400"
                            }`}
                        >
                            Acces aux produits
                        </span>
                    </div>
                </div>

                {/* Logout button / Redirect indicator */}
                <div className="mt-8 pt-6 border-t border-white/10">
                    {status === "pending" ? (
                        <button
                            onClick={logout}
                            className="group text-gray-400 hover:text-white transition-all duration-300 text-sm inline-flex items-center space-x-2"
                        >
                            <svg
                                className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform duration-300"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                />
                            </svg>
                            <span>Se deconnecter</span>
                        </button>
                    ) : (
                        <div className="flex items-center justify-center space-x-2 text-green-400">
                            <svg
                                className="w-5 h-5 animate-[bounce_1s_ease-in-out_infinite]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                                />
                            </svg>
                            <span className="text-sm font-medium">
                                Redirection vers les produits...
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom keyframes style */}
            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes fadeOutUp {
                    from {
                        opacity: 1;
                        transform: translateY(0);
                    }
                    to {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.5);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                @keyframes checkmark {
                    from {
                        stroke-dashoffset: 100;
                        opacity: 0;
                    }
                    to {
                        stroke-dashoffset: 0;
                        opacity: 1;
                    }
                }
                @keyframes confetti {
                    0% {
                        opacity: 1;
                        transform: translate(-50%, -50%) rotate(var(--rotation, 0deg)) translateY(0);
                    }
                    100% {
                        opacity: 0;
                        transform: translate(-50%, -50%) rotate(var(--rotation, 0deg)) translateY(-60px);
                    }
                }
            `}</style>
        </div>
    );
};

export default PendingApproval;
